import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "#environments/environment";
import { ReleaseNote } from "#models/release-note.model";
import { catchError, map, switchMap } from "rxjs/operators";
import { forkJoin, Observable, of } from "rxjs";

interface ExistingReleaseNoteApiItem {
  ID?: string;
  FileName?: string;
  BlobPath?: string;
  Summary?: string;
  IsDeleted?: boolean;
  DeletedBy?: string;
  DeletedDate?: string;
  CreateDate?: string;
  CreatedBy?: string;
  UpdatedDate?: string;
  UpdatedBy?: string;
}

interface UserReleaseNoteDTO {
  userID?: number;
  releaseNoteID?: number;
  firstAccessedDate?: string;
  lastAccessedDate?: string;
  accessCount?: number;
  releaseNote?: {
    id?: number;
    fileName?: string;
    blobPath?: string;
    summary?: string;
  };
  userEmail?: string;
  userFullName?: string;
}

@Injectable({
  providedIn: "root",
})
export class ReleaseNoteService {
  private readonly _readStorageKey = "release-notes-read-ids";
  private readonly _baseRoute = `${environment.apiEndpoint}/release-note`;

  constructor(private readonly _http: HttpClient) {}

  getLatest(take: number = 5, userId?: number): Observable<ReleaseNote[]> {
    if (!userId) {
      return this._fetchExistingFiles().pipe(map((items) => items.slice(0, take)));
    }

    return forkJoin({
      existing: this._fetchExistingFiles(),
      accesses: this.getUserAccesses(userId).pipe(catchError(() => of([]))),
    }).pipe(
      map(({ existing, accesses }) =>
        this.applyServerReadState(existing.slice(0, take), accesses)
      )
    );
  }

  getAll(userId?: number): Observable<ReleaseNote[]> {
    if (!userId) {
      return this._fetchExistingFiles();
    }

    return forkJoin({
      existing: this._fetchExistingFiles(),
      accesses: this.getUserAccesses(userId).pipe(catchError(() => of([]))),
    }).pipe(map(({ existing, accesses }) => this.applyServerReadState(existing, accesses)));
  }

  registerUserAccess(releaseNoteId: number, userId: number): Observable<any> {
    return this._http.post(
      `${this._baseRoute}/user-access?releaseNoteId=${releaseNoteId}&userId=${userId}`,
      null
    );
  }

  getUserAccesses(userId: number): Observable<UserReleaseNoteDTO[]> {
    return this._http.get<UserReleaseNoteDTO[]>(`${this._baseRoute}/user/${userId}`);
  }

  getReleaseNoteUsers(releaseNoteId: number): Observable<UserReleaseNoteDTO[]> {
    return this._http.get<UserReleaseNoteDTO[]>(
      `${this._baseRoute}/${releaseNoteId}/users`
    );
  }

  uploadAttachment(file: File): Observable<ReleaseNote> {
    const formData = new FormData();
    formData.append("file", file, file.name);

    return this._http
      .post<ExistingReleaseNoteApiItem>(`${this._baseRoute}/UploadAttachment`, formData)
      .pipe(
        switchMap((uploadedItem) =>
          this._fetchExistingFiles().pipe(
            map((existingNotes) =>
              this.resolveUploadedReleaseNote(uploadedItem || {}, existingNotes, file.name)
            ),
            catchError(() => of(this.normalizeItem(uploadedItem || {})))
          )
        )
      );
  }

  markAsRead(ids: string[]): void {
    if (!ids?.length) {
      return;
    }

    const current = this.getReadIds();
    ids.forEach((id) => current.add(id));

    try {
      localStorage.setItem(this._readStorageKey, JSON.stringify(Array.from(current)));
    } catch {
    }

  }

  getNumericReleaseNoteId(id: string): number | null {
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private _fetchExistingFiles(): Observable<ReleaseNote[]> {
    return this._http
      .get<ExistingReleaseNoteApiItem[]>(`${this._baseRoute}/Existing_Files`)
      .pipe(map((response) => this.normalizeList(response)));
  }

  private normalizeList(response: ExistingReleaseNoteApiItem[]): ReleaseNote[] {
    return (response || [])
      .map((item) => this.normalizeItem(item))
      .sort(
        (a, b) =>
          new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );
  }

  private normalizeItem(item: ExistingReleaseNoteApiItem): ReleaseNote {
    const fileName = String(item.FileName || "");
    const id = String(item.ID || fileName);
    const version = this.extractVersion(fileName);
    const releaseDate = this.resolveReleaseDate(item.CreateDate, item.UpdatedDate);
    const fileUrl = `${this._baseRoute}/${encodeURIComponent(fileName)}`;

    return {
      id,
      version,
      title: fileName,
      summary: String(item.Summary || ""),
      releaseDate,
      fileUrl,
      isUnread: !this.getReadIds().has(id),
    };
  }

  private extractVersion(fileName: string): string | null {
    const match = /(?:^|[^\d])v?(\d+(?:\.\d+){1,3})(?:[^\d]|$)/i.exec(
      fileName
    );
    return match?.[1] || null;
  }

  private resolveReleaseDate(...candidates: Array<string | undefined>): string {
    const minDotNetDatePrefix = "0001-01-01";

    for (const value of candidates) {
      const normalized = String(value || "").trim();
      if (!normalized || normalized.startsWith(minDotNetDatePrefix)) {
        continue;
      }

      const parsedTime = new Date(normalized).getTime();
      if (!Number.isNaN(parsedTime)) {
        return normalized;
      }
    }

    return "";
  }

  private resolveUploadedReleaseNote(
    uploadedItem: ExistingReleaseNoteApiItem,
    existingNotes: ReleaseNote[],
    uploadedFileName: string
  ): ReleaseNote {
    const normalizedNotes = existingNotes || [];
    const uploadedId = String(uploadedItem.ID || "").trim();
    const uploadedTitle = String(uploadedItem.FileName || uploadedFileName || "").trim();

    if (uploadedId && uploadedId !== "0") {
      const byId = normalizedNotes.find((note) => note.id === uploadedId);
      if (byId) {
        return byId;
      }
    }

    if (uploadedTitle) {
      const target = uploadedTitle.toLowerCase();
      const byTitle = normalizedNotes.find(
        (note) => String(note.title || "").trim().toLowerCase() === target
      );
      if (byTitle) {
        return byTitle;
      }
    }

    return this.normalizeItem(uploadedItem || {});
  }

  private applyServerReadState(
    notes: ReleaseNote[],
    accesses: UserReleaseNoteDTO[]
  ): ReleaseNote[] {
    const serverReadIds = new Set(
      (accesses || [])
        .map((item) => item.releaseNoteID ?? item.releaseNote?.id)
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value))
    );

    const localReadIds = this.getReadIds();

    return notes.map((note) => ({
      ...note,
      isUnread: !serverReadIds.has(note.id) && !localReadIds.has(note.id),
    }));
  }

  private getReadIds(): Set<string> {
    try {
      const stored = localStorage.getItem(this._readStorageKey);
      if (!stored) {
        return new Set<string>();
      }

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        return new Set<string>();
      }

      return new Set<string>(parsed.map((value) => String(value)));
    } catch {
      return new Set<string>();
    }
  }
}
